import Title from "@/components/common/Title";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { selectOrganisationUsersWithFavouritesByOrganisationIdQueryOptions } from "@/features/admin/api/selectOrganisationsUsersWithFavourites";
import { services as s } from "@/services";
import { useNav } from "@/utils/nav";
import { createFileRoute } from "@tanstack/react-router";
import { organisationByIdQueryOptions } from "../../../../features/auth/api/selectOrganisationById";
import {
	selectProfiles,
	selectProfilesQueryOptions,
} from "../../../../features/profile/api/selectProfiles";
import { useAuth } from "../../../../utils/auth";

interface Profile {
	user_id: string;
	name: string;
	email: string;
	roles: string[];
	favourite_users: Array<{
		profiles: {
			user_id: string;
			name: string;
			email: string;
			roles: string[];
			organisations?: Array<{
				organisation_id: string;
			}>;
		};
	}>;
}

interface OrganisationUser {
	profiles: Profile;
}

// Main component
const OrganisationPage = () => {
	const auth = useAuth();
	const { organisationId } = Route.useParams();
	useNav([
		{ label: "Profile", route: { to: "/profile" } },
		{ label: "Organisation", route: { to: "/organisation/$organisationId" } },
	]);

	const { organisation, clinicians, managers, secretaries } =
		Route.useLoaderData();

	return (
		<section className="lg-width">
			<Title words={organisation.organisation_name} />

			{clinicians.length > 0 &&
				(auth.roles.isClinician || auth.roles.isSecretary) && (
					<Card className="mb-6">
						<CardHeader>
							<CardTitle>{organisation.organisation_name} Clinicians</CardTitle>
						</CardHeader>
						<CardContent>
							<Accordion type="single" collapsible>
								{clinicians.map(({ name, email, user_id, roles }) => (
									<AccordionItem key={user_id} value={user_id}>
										<AccordionTrigger>
											<div className="flex flex-col justify-between">
												<span>
													<strong className="text-slate-800">{name}</strong> (
													{s.capitaliseSentence(roles.join(", "))})
												</span>{" "}
												<span className="text-slate-600">{email}</span>
											</div>
										</AccordionTrigger>
										<AccordionContent className="pl-4 text-sm">
											<h5>No Associated Secretaries</h5>
										</AccordionContent>
									</AccordionItem>
								))}
							</Accordion>
						</CardContent>
					</Card>
				)}

			{managers.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle>{organisation.organisation_name} Managers</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{managers.map(({ name, email, user_id, roles }) => (
								<div
									key={user_id}
									className="flex flex-col justify-between pb-4 border-b"
								>
									<span>
										<strong className="text-slate-800 text-lg">{name}</strong> (
										{s.capitaliseSentence(roles.join(", "))})
									</span>
									<span className="text-md text-slate-600">{email}</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{secretaries.length > 0 && (
				<Card className="mt-6">
					<CardHeader>
						<CardTitle>{organisation.organisation_name} Secretaries</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{secretaries.map(({ name, email, user_id, roles }) => (
								<div
									key={user_id}
									className="flex flex-col justify-between pb-4 border-b"
								>
									<span>
										<strong className="text-slate-800 text-lg">{name}</strong> (
										{s.capitaliseSentence(roles.join(", "))})
									</span>
									<span className="text-md text-slate-600">{email}</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}
		</section>
	);
};

export default OrganisationPage;

export const Route = createFileRoute("/_app/organisation/$organisationId/")({
	loader: async ({ context, params }) => {
		const { queryClient } = context;
		const organisationId = params.organisationId;

		const organisation = await queryClient.ensureQueryData(
			organisationByIdQueryOptions(organisationId),
		);

		const allProfiles = await queryClient.ensureQueryData(
			selectProfilesQueryOptions(),
		);

		const orgProfiles = allProfiles.filter((p) =>
			p.organisations?.some((o) => o.organisation_id === organisationId),
		);

		// Filter clinicians and managers
		const clinicians = orgProfiles
			.filter((user) => user.roles?.includes("CLINICIAN"))
			.map((user) => ({
				name: user.name || "",
				email: user.email || "",
				user_id: user.user_id || "",
				roles: user.roles || [],
			}));

		console.log(clinicians);

		const managers = orgProfiles
			.filter((user) => user.roles?.includes("MANAGER"))
			.map((user) => ({
				name: user.name || "",
				email: user.email || "",
				user_id: user.user_id || "",
				roles: user.roles || [],
			}));

		const secretaries = orgProfiles
			.filter((user) => user.roles?.includes("SECRETARY"))
			.map((user) => ({
				name: user.name || "",
				email: user.email || "",
				user_id: user.user_id || "",
				roles: user.roles || [],
			}));

		return { organisation, clinicians, managers, secretaries };
	},
	component: OrganisationPage,
});
